import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SectionTitle } from "@/components/SectionTitle";
import { toast } from "sonner";
import { CheckCircle, Warning, ShieldCheck, DownloadSimple } from "@phosphor-icons/react";

function SignaturePad({ canvasRef, onChange }) {
  const drawing = useRef(false);
  const last = useRef(null);
  const point = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches && e.touches[0] ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = point(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.strokeStyle = "#FF5722"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; onChange && onChange(true);
  };
  const end = () => { drawing.current = false; };
  return (
    <canvas
      ref={canvasRef} width={600} height={180}
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      data-testid="waiver-signature-canvas"
      className="w-full h-40 bg-[#1A1A1A] border border-white/15 touch-none cursor-crosshair"
    />
  );
}

export default function Waiver() {
  const { settings } = useSettings();
  const DIVISIONS = settings.divisions || [];
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [roster, setRoster] = useState([]);
  const [playerId, setPlayerId] = useState("");
  const [signature, setSignature] = useState("");
  const [sigMode, setSigMode] = useState("type");
  const [hasDrawn, setHasDrawn] = useState(false);
  const sigCanvasRef = useRef(null);
  const [agree, setAgree] = useState(false);
  const [isMinor, setIsMinor] = useState(false);
  const [guardianName, setGuardianName] = useState("");
  const [guardianSignature, setGuardianSignature] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [receiptToken, setReceiptToken] = useState("");

  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!teamId) { setRoster([]); return; }
    api.get(`/waiver/roster?team_id=${teamId}`).then((r) => setRoster(r.data)).catch(() => {});
    setPlayerId("");
  }, [teamId]);

  const player = roster.find((p) => p.id === playerId);

  const submit = async (e) => {
    e.preventDefault();
    if (!player) { toast.error("Please select your name"); return; }
    if (!player.paid_in_full) { toast.error("Your league fee must be paid in full first."); return; }
    let signatureText = signature.trim();
    let signatureImage = "";
    if (sigMode === "draw") {
      if (!hasDrawn) { toast.error("Please draw your signature"); return; }
      signatureImage = sigCanvasRef.current ? sigCanvasRef.current.toDataURL("image/png") : "";
      signatureText = signatureText || player.name;
    } else if (!signatureText) {
      toast.error("Please type your full name to sign"); return;
    }
    if (isMinor && (!guardianName.trim() || !guardianSignature.trim())) { toast.error("Parent/guardian name and signature are required for players under 18"); return; }
    if (!agree) { toast.error("You must agree to the terms"); return; }
    setLoading(true);
    try {
      const res = await api.post("/waiver/sign", {
        player_id: playerId,
        signature: signatureText,
        signature_image: signatureImage,
        is_minor: isMinor,
        guardian_name: isMinor ? guardianName.trim() : "",
        guardian_signature: isMinor ? guardianSignature.trim() : "",
      });
      setReceiptToken(res.data?.receipt_token || "");
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (done) {
    const downloadReceipt = async () => {
      if (!receiptToken) { toast.error("Receipt link unavailable"); return; }
      try {
        const res = await api.get(`/waiver/receipt/${playerId}?token=${encodeURIComponent(receiptToken)}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = `waiver-${signature.trim().replace(/\s+/g, "_") || "receipt"}.pdf`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      } catch { toast.error("Could not download the receipt"); }
    };
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="waiver-done">
        <CheckCircle size={80} weight="fill" className="text-[#00E676] mx-auto mb-6" />
        <h2 className="font-display text-5xl uppercase">Waiver Signed</h2>
        <p className="text-white/60 mt-3 mb-8">Thank you, {signature.trim()}. Your signed waiver has been recorded. You're all set to play once your team is scheduled.</p>
        <button onClick={downloadReceipt} data-testid="download-receipt-btn" className="inline-flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide px-6 py-3 transition-all duration-200">
          <DownloadSimple size={18} weight="bold" /> Download Receipt (PDF)
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="waiver-page">
      <SectionTitle overline="Player Compliance" title="Sign Your Waiver" sub="Every player must sign the IEFSC Release &amp; Waiver of Liability before playing. Your league fee must be paid in full first." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Your Team</label>
          <select value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="waiver-team-select" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all">
            <option value="">Select your team…</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.division || "Open"})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Your Name</label>
          <select value={playerId} onChange={(e) => setPlayerId(e.target.value)} disabled={!teamId} data-testid="waiver-player-select" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all disabled:opacity-40">
            <option value="">{teamId ? "Select your name…" : "Pick a team first"}</option>
            {roster.map((p) => <option key={p.id} value={p.id}>{p.number != null ? `#${p.number} ` : ""}{p.name}{p.waiver_signed ? " ✓ signed" : ""}</option>)}
          </select>
        </div>
      </div>

      {player && player.waiver_signed && (
        <div className="border border-[#00E676]/40 bg-[#00E676]/10 p-4 mb-6 flex items-center gap-3" data-testid="waiver-already">
          <CheckCircle size={22} weight="fill" className="text-[#00E676] shrink-0" />
          <p className="text-sm text-white/80">You've already signed your waiver. No further action needed.</p>
        </div>
      )}

      {player && !player.waiver_signed && !player.paid_in_full && (
        <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-4 mb-6 flex items-center gap-3" data-testid="waiver-unpaid">
          <Warning size={22} weight="fill" className="text-[#FF3B30] shrink-0" />
          <p className="text-sm text-white/80">Your league fee isn't fully paid yet. Please pay your fee in full through your team admin before signing the waiver.</p>
        </div>
      )}

      <div className="border border-white/10 bg-[#121212] p-6 mb-6" data-testid="waiver-text-box">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck size={22} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">Waiver Agreement</h3></div>
        <div className="max-h-72 overflow-y-auto whitespace-pre-line text-white/70 text-sm leading-relaxed pr-2">
          {settings.waiver_text || "Loading waiver…"}
        </div>
      </div>

      {player && !player.waiver_signed && player.paid_in_full && (
        <form onSubmit={submit} className="border border-white/10 bg-[#121212] p-6 space-y-4" data-testid="waiver-sign-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Full Name</label>
              <div data-testid="waiver-fullname" className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 text-sm text-white/80 uppercase">{player.name}</div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Date (auto-recorded)</label>
              <div data-testid="waiver-date" className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 text-sm text-white/80">{new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/60">Digital Signature</label>
              <div className="flex border border-white/15 text-xs" data-testid="waiver-sig-mode">
                <button type="button" onClick={() => setSigMode("type")} data-testid="waiver-sig-mode-type" className={`px-3 py-1 uppercase font-bold tracking-wide transition-colors ${sigMode === "type" ? "bg-[#FF5722] text-black" : "text-white/60 hover:text-white"}`}>Type</button>
                <button type="button" onClick={() => setSigMode("draw")} data-testid="waiver-sig-mode-draw" className={`px-3 py-1 uppercase font-bold tracking-wide transition-colors ${sigMode === "draw" ? "bg-[#FF5722] text-black" : "text-white/60 hover:text-white"}`}>Draw</button>
              </div>
            </div>
            {sigMode === "type" ? (
              <input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Type your full legal name" data-testid="waiver-signature-input" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-lg font-display tracking-wide focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
            ) : (
              <div>
                <SignaturePad canvasRef={sigCanvasRef} onChange={setHasDrawn} />
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-white/40">Sign above with your mouse, finger, or stylus</span>
                  <button type="button" onClick={() => { const c = sigCanvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasDrawn(false); }} data-testid="waiver-signature-clear" className="text-xs uppercase font-bold text-white/60 hover:text-[#FF5722] transition-colors">Clear</button>
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-white/10 pt-4">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={isMinor} onChange={(e) => setIsMinor(e.target.checked)} data-testid="waiver-minor-checkbox" className="w-5 h-5 accent-[#FF5722] mt-0.5 shrink-0" />
              <span className="text-sm text-white/75">This player is <span className="font-semibold text-white">under 18 years of age</span> — a parent or legal guardian must provide consent and sign below.</span>
            </label>
            {isMinor && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-testid="waiver-guardian-fields">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Parent / Guardian Full Name</label>
                  <input value={guardianName} onChange={(e) => setGuardianName(e.target.value)} placeholder="Guardian's full name" data-testid="waiver-guardian-name" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Parent / Guardian Signature</label>
                  <input value={guardianSignature} onChange={(e) => setGuardianSignature(e.target.value)} placeholder="Type guardian's full name" data-testid="waiver-guardian-signature" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-lg font-display tracking-wide focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all" />
                </div>
              </div>
            )}
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="waiver-agree-checkbox" className="w-5 h-5 accent-[#FF5722] mt-0.5 shrink-0" />
            <span className="text-sm text-white/75">I have read, understood, and agree to the Release and Waiver of Liability Agreement.</span>
          </label>
          <p className="text-xs text-white/40">Your date &amp; time, IP address, and device information are automatically recorded with your signature. Once submitted, this waiver is locked and cannot be edited.</p>
          <button type="submit" disabled={loading} data-testid="waiver-submit-btn" className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3.5 transition-all duration-200">
            {loading ? "Submitting…" : "Sign & Submit Waiver"}
          </button>
        </form>
      )}
    </div>
  );
}
